import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faPhone,
  faHistory,
  faFolder,
  faBriefcase,
  faMapMarkerAlt,
  faSearch,
  faArrowLeft,
  faRobot,
} from '@fortawesome/free-solid-svg-icons';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  ListGroup,
  Button,
  InputGroup,
  Badge,
  Alert,
} from 'react-bootstrap';
import { ClientService } from '../../../../services/clientService';
import { ClientFolderService } from '../../../../services/clientFolderService';
import { BolnaService } from '../../../../services/bolnaService';
import {
  AICallPopup,
  CallStatusModal,
  CallHistoryModal,
} from '../../../clients/candidate-information/popup-container/index';
import Loader from 'react-spinner-loader';
import SigniteButton from '../../../ui-elements/signite-button/signiteButton';

const PAGE_SIZE = 9;
const FOLDER_PAGE_SIZE = 10;

const AdminAutomatedInterviews = () => {
  // ---------- Client selection state ----------
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);

  // ---------- Folder state ----------
  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);

  const [folderType, setFolderType] = useState('CURATED');
  const [folderPage, setFolderPage] = useState(0);
  const [folderTotalPages, setFolderTotalPages] = useState(0);
  const [folderTotalEntries, setFolderTotalEntries] = useState(0);

  // ---------- Candidate state ----------
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalEntries, setTotalEntries] = useState(0);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');

  // ---------- AI Call state ----------
  const [isAICallPopupVisible, setAICallPopupVisible] = useState(false);
  const [selectedCandidateForCall, setSelectedCandidateForCall] =
    useState(null);

  const [currentExecutionId, setCurrentExecutionId] = useState(null);
  const [callStatusSource, setCallStatusSource] = useState(null);
  const [isCallStatusModalVisible, setCallStatusModalVisible] =
    useState(false);
  const [callStatusData, setCallStatusData] = useState(null);
  const [isCallStatusLoading, setCallStatusLoading] = useState(false);

  const [isCallHistoryModalVisible, setCallHistoryModalVisible] =
    useState(false);
  const [callHistoryData, setCallHistoryData] = useState([]);
  const [isCallHistoryLoading, setCallHistoryLoading] = useState(false);
  const [callHistoryScrollTop, setCallHistoryScrollTop] = useState(0);
  const [callHistoryCandidate, setCallHistoryCandidate] = useState(null);

  const [errorMessage, setErrorMessage] = useState('');

  // ---------- Fetch clients ----------
  useEffect(() => {
    const fetchClients = async () => {
      setClientsLoading(true);

      try {
        const res = await ClientService.getAllClients(
          0,
          100,
          clientSearch
        );

        const list = (
          res.data?.data?.clients ||
          res.data?.clients ||
          res.data?.data ||
          []
        ).filter(
          (client) =>
            client.clientStatus === 'ACTIVE' ||
            !client.clientStatus
        );

        setClients(list);
      } catch (err) {
        console.error('Error fetching clients:', err);
        setClients([]);
      } finally {
        setClientsLoading(false);
      }
    };

    fetchClients();
  }, [clientSearch]);

  // ---------- Fetch folders ----------
  useEffect(() => {
    if (!selectedClient) {
      setFolders([]);
      setSelectedFolder(null);
      setFolderPage(0);
      setFolderTotalPages(0);
      setFolderTotalEntries(0);
      return;
    }

    const loadFolders = async () => {
      setFoldersLoading(true);

      try {
        const res =
          folderType === 'CURATED'
            ? await ClientFolderService.listCuratedFolders(
              selectedClient.clientId,
              'CURATED',
              folderPage,
              FOLDER_PAGE_SIZE
            )
            : await ClientFolderService.listShortlistedFolders(
              selectedClient.clientId,
              'SHORTLISTED',
              folderPage,
              FOLDER_PAGE_SIZE
            );

        const responseData = res?.data;

        const list = Array.isArray(responseData?.folders)
          ? responseData.folders
          : [];

        const mappedFolders = list.map((folder) => ({
          folderId: folder.folderId,
          folderName: folder.name || folder.folderName || '',
          clientId: folder.clientId,
          type: folder.type,
        }));

        setFolders(mappedFolders);

        setFolderTotalEntries(responseData?.totalCount || 0);
        setFolderTotalPages(
          responseData?.pageCount ||
          Math.ceil(
            (responseData?.totalCount || 0) / FOLDER_PAGE_SIZE
          )
        );
      } catch (err) {
        console.error('Error loading folders:', err);
        setFolders([]);
        setFolderTotalPages(0);
        setFolderTotalEntries(0);
      } finally {
        setFoldersLoading(false);
      }
    };

    loadFolders();
  }, [selectedClient, folderType, folderPage]);

  // ---------- Fetch candidates ----------
  useEffect(() => {
    if (!selectedFolder || !selectedClient) {
      return;
    }

    const loadCandidates = async () => {
      setCandidatesLoading(true);

      try {
        let res;

        if (folderType === 'CURATED') {
          res = await ClientFolderService.getCuratedCandidates(
            selectedClient.clientId,
            selectedFolder.folderId,
            currentPage,
            PAGE_SIZE,
            searchKeyword
          );
        } else {
          res = await ClientService.getShortlistedCandidates(
            selectedFolder.folderId,
            currentPage,
            PAGE_SIZE,
            searchKeyword,
            selectedClient.clientId
          );
        }

        const responseData = res?.data ?? res;

        const list =
          folderType === 'CURATED'
            ? responseData?.curatedList || []
            : responseData?.shortlists || [];

        const mapped = list
          .map((item) => {
            const candidate = item?.candidate;

            if (!candidate?.candidateId) {
              return null;
            }

            const personal =
              candidate?.candidate?.personalDetails || {};
            const work =
              candidate?.candidate?.workExperience || {};
            const compensation =
              candidate?.candidate?.compensationDetails || {};

            return {
              candidateId: candidate.candidateId,
              firstName: personal.firstName || '',
              lastName: personal.lastName || '',
              phone: personal.primaryPhoneNumber || '',
              position:
                work?.currentRole?.designation || '',
              company: work?.currentRole?.orgName || '',
              industry: work?.currentRole?.industry || '',
              location: work?.currentRole?.location || '',
              experience: work.totalYrs
                ? `${work.totalYrs} yrs`
                : '',
              salary: compensation?.total
                ? `₹ ${compensation.total} L / annum`
                : '',
            };
          })
          .filter(Boolean);

        setCandidates(mapped);

        const totalCount = responseData?.totalCount || 0;

        setTotalEntries(totalCount);

        setTotalPages(
          responseData?.totalPages ||
          responseData?.pageCount ||
          Math.ceil(totalCount / PAGE_SIZE)
        );
      } catch (err) {
        console.error('Error loading candidates:', err);
        setCandidates([]);
        setTotalPages(0);
        setTotalEntries(0);
      } finally {
        setCandidatesLoading(false);
      }
    };

    loadCandidates();
  }, [
    selectedFolder,
    currentPage,
    searchKeyword,
    selectedClient,
    folderType,
  ]);

  // ---------- AI Call handlers ----------
  const handleInitiateCall = (candidate) => {
    setSelectedCandidateForCall(candidate);
    setAICallPopupVisible(true);
  };

  const handleAICallInitiated = useCallback((callResponse) => {
    if (callResponse?.executionId) {
      setCurrentExecutionId(callResponse.executionId);
      setCallStatusSource('direct');
      fetchCallStatus(callResponse.executionId);
      setCallStatusModalVisible(true);
    }
  }, []);

  const fetchCallStatus = async (executionId) => {
    try {
      setCallStatusLoading(true);

      const response =
        await BolnaService.getCallStatus(executionId);

      setCallStatusData(response.data);
    } catch (err) {
      console.error('Failed to fetch call status:', err);
      setErrorMessage(
        'Failed to fetch call status. Please try again.'
      );
    } finally {
      setCallStatusLoading(false);
    }
  };

  const handleRefreshCallStatus = async () => {
    if (!currentExecutionId) {
      return;
    }

    try {
      setCallStatusLoading(true);

      const response =
        await BolnaService.refreshCallStatus(
          currentExecutionId
        );

      setCallStatusData(response.data);
    } catch (err) {
      console.error('Failed to refresh call status:', err);
      setErrorMessage(
        'Failed to refresh call status. Please try again.'
      );
    } finally {
      setCallStatusLoading(false);
    }
  };

  const handleCallStatusClose = () => {
    setCallStatusModalVisible(false);

    if (callStatusSource === 'history') {
      setCallHistoryModalVisible(true);
    }

    setCallStatusSource(null);
  };

  const fetchCallHistory = async (candidateId) => {
    if (!candidateId) {
      return;
    }

    try {
      setCallHistoryLoading(true);

      const response = await BolnaService.getCalls(candidateId);

      setCallHistoryData(response.data?.content || []);
    } catch (err) {
      console.error('Failed to fetch call history:', err);
    } finally {
      setCallHistoryLoading(false);
    }
  };

  const handleViewCallHistory = (candidate) => {
    setCallHistoryCandidate(candidate);
    fetchCallHistory(candidate.candidateId);
    setCallHistoryModalVisible(true);
  };

  const handleViewCallDetailsFromHistory = (
    executionId,
    scrollTop
  ) => {
    setCallHistoryScrollTop(scrollTop);
    setCurrentExecutionId(executionId);
    setCallStatusSource('history');
    setCallHistoryModalVisible(false);
    fetchCallStatus(executionId);
    setCallStatusModalVisible(true);
  };

  const handleRefreshCallFromHistory = async (executionId) => {
    try {
      const response =
        await BolnaService.refreshCallStatus(executionId);

      setCallHistoryData((prev) =>
        prev.map((call) =>
          call.executionId === executionId
            ? response.data
            : call
        )
      );
    } catch (err) {
      console.error('Failed to refresh call:', err);
    }
  };

  // ---------- Candidate search ----------
  const triggerSearch = () => {
    setCurrentPage(0);
    setSearchKeyword(candidateSearch.trim());
  };

  // ---------- Client selection ----------
  const handleClientSelect = (client) => {
    setSelectedClient(client);

    setSelectedFolder(null);
    setFolders([]);
    setCandidates([]);

    setFolderPage(0);
    setFolderTotalPages(0);
    setFolderTotalEntries(0);

    setCurrentPage(0);
    setTotalPages(0);
    setTotalEntries(0);

    setSearchKeyword('');
    setCandidateSearch('');
  };

  // ---------- Folder type selection ----------
  const handleFolderTypeChange = (type) => {
    setFolderType(type);

    setFolderPage(0);
    setSelectedFolder(null);
    setFolders([]);

    setCurrentPage(0);
    setCandidates([]);

    setSearchKeyword('');
    setCandidateSearch('');

    setTotalPages(0);
    setTotalEntries(0);
  };

  // ---------- Folder pagination ----------
  const renderFolderPagination = () => {
    if (folderTotalPages <= 1) {
      return null;
    }

    return (
      <div className="d-flex justify-content-center align-items-center gap-2 mt-4">
        <SigniteButton
          variant="light"
          onClick={() => setFolderPage(0)}
          disabled={folderPage === 0}
        >
          {'<<'}
        </SigniteButton>

        <SigniteButton
          variant="light"
          onClick={() =>
            setFolderPage((page) => Math.max(0, page - 1))
          }
          disabled={folderPage === 0}
        >
          {'<'}
        </SigniteButton>

        <span className="mx-2 text-muted fw-bold">
          {folderPage + 1} / {folderTotalPages}
        </span>

        <SigniteButton
          variant="light"
          onClick={() =>
            setFolderPage((page) =>
              Math.min(folderTotalPages - 1, page + 1)
            )
          }
          disabled={
            folderPage >= folderTotalPages - 1
          }
        >
          {'>'}
        </SigniteButton>

        <SigniteButton
          variant="light"
          onClick={() =>
            setFolderPage(folderTotalPages - 1)
          }
          disabled={
            folderPage >= folderTotalPages - 1
          }
        >
          {'>>'}
        </SigniteButton>
      </div>
    );
  };

  // ---------- Candidate pagination ----------
  const renderPagination = () => {
    if (totalPages <= 1) {
      return null;
    }

    return (
      <div className="d-flex justify-content-center align-items-center gap-2 mt-4">
        <SigniteButton
          variant="light"
          onClick={() => setCurrentPage(0)}
          disabled={currentPage === 0}
        >
          {'<<'}
        </SigniteButton>

        <SigniteButton
          variant="light"
          onClick={() =>
            setCurrentPage((page) =>
              Math.max(0, page - 1)
            )
          }
          disabled={currentPage === 0}
        >
          {'<'}
        </SigniteButton>

        <span className="mx-2 text-muted fw-bold">
          {currentPage + 1} / {totalPages}
        </span>

        <SigniteButton
          variant="light"
          onClick={() =>
            setCurrentPage((page) =>
              Math.min(totalPages - 1, page + 1)
            )
          }
          disabled={
            currentPage >= totalPages - 1
          }
        >
          {'>'}
        </SigniteButton>

        <SigniteButton
          variant="light"
          onClick={() =>
            setCurrentPage(totalPages - 1)
          }
          disabled={
            currentPage >= totalPages - 1
          }
        >
          {'>>'}
        </SigniteButton>
      </div>
    );
  };

  return (
    <Container fluid>
      <Loader
        show={
          clientsLoading ||
          candidatesLoading ||
          foldersLoading
        }
        type="body"
        stack="vertical"
        message="Loading Data"
      />

      {/* Global Error Banner */}
      {errorMessage && (
        <Alert
          variant="danger"
          onClose={() => setErrorMessage('')}
          dismissible
          className="mt-3"
        >
          {errorMessage}
        </Alert>
      )}

      {/* Header Card */}
      <Card className="mt-2 mb-3 shadow-sm border-0">
        <Card.Body className="d-flex align-items-center">
          <div
            className="text-white p-3 rounded me-3 d-flex align-items-center justify-content-center"
            style={{ backgroundColor: '#ff6a00' }}
          >
            <FontAwesomeIcon icon={faRobot} size="lg" />
          </div>

          <div>
            <h4 className="mb-1 fw-bold text-dark">
              AI Interviews
            </h4>

            <p className="mb-0 text-muted">
              Select an active client to initiate AI-driven
              screening calls on their behalf.
            </p>
          </div>
        </Card.Body>
      </Card>

      <Row>
        {/* Left Panel: Clients */}
        <Col md={3}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Header className="bg-white py-3 border-bottom-0">
              <h6 className="mb-2 fw-bold text-dark">
                <FontAwesomeIcon
                  icon={faBuilding}
                  className="me-2 text-muted"
                />
                Clients
              </h6>

              <InputGroup size="sm">
                <InputGroup.Text className="bg-light border-end-0">
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="text-muted"
                  />
                </InputGroup.Text>

                <Form.Control
                  className="bg-light border-start-0 shadow-none"
                  placeholder="Search clients..."
                  value={clientSearch}
                  onChange={(e) =>
                    setClientSearch(e.target.value)
                  }
                />
              </InputGroup>
            </Card.Header>

            <ListGroup
              variant="flush"
              className="overflow-auto"
              style={{ maxHeight: '600px' }}
            >
              {!clientsLoading && clients.length === 0 && (
                <div className="p-4 text-center text-muted">
                  <small>
                    No active clients found
                  </small>
                </div>
              )}

              {clients.map((client) => {
                const isSelected =
                  selectedClient?.clientId ===
                  client.clientId;

                return (
                  <ListGroup.Item
                    key={client.clientId}
                    action
                    active={isSelected}
                    onClick={() =>
                      handleClientSelect(client)
                    }
                    className={`border-0 py-3 ${isSelected
                      ? 'border-start border-4'
                      : ''
                      }`}
                    style={{
                      cursor: 'pointer',
                      ...(isSelected && {
                        color: '#ff6a00',
                        backgroundColor: '#fff3e6',
                        borderLeft:
                          '4px solid #ff6a00',
                      }),
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <span
                        className="fw-semibold"
                        style={{
                          fontSize: '0.9rem',
                        }}
                      >
                        {client.companyName}
                      </span>

                      {isSelected && (
                        <FontAwesomeIcon
                          icon={faArrowLeft}
                          className="ms-2 small"
                          style={{
                            transform:
                              'rotate(180deg)',
                          }}
                        />
                      )}
                    </div>
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          </Card>
        </Col>

        {/* Right Panel */}
        <Col md={9}>
          <Card
            className="shadow-sm border-0 h-100"
            style={{ minHeight: '600px' }}
          >
            {selectedClient ? (
              <>
                <Card.Header className="bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    <span
                      className="px-3 py-2 fs-6 fw-normal rounded-pill text-white"
                      style={{
                        background: '#ff6a00',
                      }}
                    >
                      {selectedClient.companyName}
                    </span>

                    {selectedFolder && (
                      <>
                        <span className="text-muted fw-bold px-1">
                          ›
                        </span>

                        <Badge
                          bg="secondary"
                          text="light"
                          className="px-3 py-2 fs-6 fw-normal rounded-pill"
                        >
                          <FontAwesomeIcon
                            icon={faFolder}
                            className="me-2"
                          />
                          {selectedFolder.folderName}
                        </Badge>
                      </>
                    )}
                  </div>

                  {selectedFolder && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setSelectedFolder(null);
                        setCandidates([]);
                        setCurrentPage(0);
                        setSearchKeyword('');
                        setCandidateSearch('');
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faArrowLeft}
                        className="me-2"
                      />
                      Back to Folders
                    </Button>
                  )}
                </Card.Header>

                <Card.Body className="p-4 bg-light">
                  {/* FOLDERS VIEW */}
                  {!selectedFolder && (
                    <div>
                      <div className="d-flex gap-2 mb-3">
                        <Button
                          size="sm"
                          onClick={() =>
                            handleFolderTypeChange(
                              'CURATED'
                            )
                          }
                          style={{
                            background:
                              folderType === 'CURATED'
                                ? '#ff6a00'
                                : '#e9ecef',
                            color:
                              folderType === 'CURATED'
                                ? '#fff'
                                : '#000',
                            border: 'none',
                          }}
                        >
                          Curated Candidates
                        </Button>

                        <Button
                          size="sm"
                          onClick={() =>
                            handleFolderTypeChange(
                              'SHORTLISTED'
                            )
                          }
                          style={{
                            background:
                              folderType ===
                                'SHORTLISTED'
                                ? '#ff6a00'
                                : '#e9ecef',
                            color:
                              folderType ===
                                'SHORTLISTED'
                                ? '#fff'
                                : '#000',
                            border: 'none',
                          }}
                        >
                          Shortlisted Candidates
                        </Button>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="mb-0 text-dark fw-bold">
                          {folderType ===
                            'SHORTLISTED'
                            ? 'Shortlisted Folders'
                            : 'Curated Folders'}
                        </h5>

                        {folderTotalEntries > 0 && (
                          <span className="text-muted small">
                            {folderTotalEntries}{' '}
                            folder
                            {folderTotalEntries !== 1
                              ? 's'
                              : ''}
                          </span>
                        )}
                      </div>

                      {!foldersLoading &&
                        folders.length === 0 ? (
                        <div className="text-center p-5 bg-white rounded shadow-sm">
                          <FontAwesomeIcon
                            icon={faFolder}
                            size="3x"
                            className="text-muted mb-3 opacity-50"
                          />

                          <h6 className="text-muted">
                            {folderType ===
                              'SHORTLISTED'
                              ? 'No shortlisted folders found.'
                              : 'No curated folders found.'}
                          </h6>
                        </div>
                      ) : (
                        <>
                          <Row className="g-3">
                            {folders.map((folder) => (
                              <Col
                                md={4}
                                key={folder.folderId}
                              >
                                <Card
                                  className="h-100 shadow-sm border-0 text-center py-4 cursor-pointer align-items-center justify-content-center custom-folder-card"
                                  onClick={() => {
                                    setSelectedFolder(
                                      folder
                                    );
                                    setCurrentPage(0);
                                    setCandidates([]);
                                    setSearchKeyword('');
                                    setCandidateSearch('');
                                  }}
                                  style={{
                                    cursor: 'pointer',
                                    transition:
                                      'all 0.2s',
                                    backgroundColor:
                                      '#fff',
                                  }}
                                  onMouseEnter={(e) =>
                                    e.currentTarget.classList.add(
                                      'shadow'
                                    )
                                  }
                                  onMouseLeave={(e) =>
                                    e.currentTarget.classList.remove(
                                      'shadow'
                                    )
                                  }
                                >
                                  <FontAwesomeIcon
                                    icon={faFolder}
                                    size="2x"
                                    className="text-warning mb-2"
                                  />

                                  <span className="fw-semibold text-dark">
                                    {folder.folderName}
                                  </span>
                                </Card>
                              </Col>
                            ))}
                          </Row>

                          {renderFolderPagination()}
                        </>
                      )}
                    </div>
                  )}

                  {/* CANDIDATES VIEW */}
                  {selectedFolder && (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <span className="text-muted fw-semibold">
                          Showing {totalEntries}{' '}
                          candidate
                          {totalEntries !== 1
                            ? 's'
                            : ''}
                        </span>

                        <InputGroup
                          size="sm"
                          style={{
                            width: '250px',
                          }}
                        >
                          <Form.Control
                            placeholder="Search candidates..."
                            value={candidateSearch}
                            onChange={(e) => {
                              const value =
                                e.target.value;

                              setCandidateSearch(
                                value
                              );

                              if (
                                value.trim() === ''
                              ) {
                                setSearchKeyword('');
                                setCurrentPage(0);
                              }
                            }}
                            onKeyDown={(e) =>
                              e.key === 'Enter' &&
                              triggerSearch()
                            }
                          />

                          <Button
                            onClick={triggerSearch}
                            style={{
                              backgroundColor:
                                '#ff6a00',
                              borderColor:
                                '#ff6a00',
                              color: '#fff',
                            }}
                          >
                            Search
                          </Button>
                        </InputGroup>
                      </div>

                      {!candidatesLoading &&
                        candidates.length === 0 ? (
                        <div className="text-center p-5 bg-white rounded shadow-sm">
                          <h6 className="text-muted">
                            No candidates match your
                            search in this folder.
                          </h6>
                        </div>
                      ) : (
                        <Row className="g-3">
                          {candidates.map(
                            (candidate) => (
                              <Col
                                lg={4}
                                md={6}
                                key={
                                  candidate.candidateId
                                }
                              >
                                <Card
                                  className="h-100 shadow-sm"
                                  style={{
                                    border:
                                      '1px solid #ff6a00',
                                    backgroundColor:
                                      '#fff',
                                    transition: '0.3s',
                                    cursor: 'pointer',
                                  }}
                                >
                                  <Card.Body className="d-flex flex-column">
                                    <div className="mb-3">
                                      <h6 className="fw-bold mb-1 text-dark fs-5">
                                        {
                                          candidate.firstName
                                        }{' '}
                                        {
                                          candidate.lastName
                                        }
                                      </h6>

                                      <small className="text-muted">
                                        {
                                          candidate.candidateId
                                        }
                                      </small>
                                    </div>

                                    <div className="flex-grow-1 mb-4">
                                      {candidate.phone && (
                                        <p
                                          className="mb-2 text-secondary"
                                          style={{
                                            fontSize:
                                              '0.85rem',
                                          }}
                                        >
                                          <FontAwesomeIcon
                                            icon={faPhone}
                                            className="me-2 text-muted"
                                            style={{
                                              width:
                                                '14px',
                                            }}
                                          />
                                          {
                                            candidate.phone
                                          }
                                        </p>
                                      )}

                                      {candidate.position && (
                                        <p
                                          className="mb-2 text-secondary"
                                          style={{
                                            fontSize:
                                              '0.85rem',
                                          }}
                                        >
                                          <FontAwesomeIcon
                                            icon={
                                              faBriefcase
                                            }
                                            className="me-2 text-muted"
                                            style={{
                                              width:
                                                '14px',
                                            }}
                                          />

                                          <span className="fw-semibold text-dark">
                                            {
                                              candidate.position
                                            }
                                          </span>

                                          {candidate.company && (
                                            <span>
                                              {' '}
                                              at{' '}
                                              {
                                                candidate.company
                                              }
                                            </span>
                                          )}
                                        </p>
                                      )}

                                      {candidate.location && (
                                        <p
                                          className="mb-2 text-secondary"
                                          style={{
                                            fontSize:
                                              '0.85rem',
                                          }}
                                        >
                                          <FontAwesomeIcon
                                            icon={
                                              faMapMarkerAlt
                                            }
                                            className="me-2 text-muted"
                                            style={{
                                              width:
                                                '14px',
                                            }}
                                          />
                                          {
                                            candidate.location
                                          }
                                        </p>
                                      )}

                                      {candidate.experience && (
                                        <p
                                          className="mb-0 text-secondary"
                                          style={{
                                            fontSize:
                                              '0.85rem',
                                          }}
                                        >
                                          <FontAwesomeIcon
                                            icon={
                                              faBriefcase
                                            }
                                            className="me-2 text-muted"
                                            style={{
                                              width:
                                                '14px',
                                            }}
                                          />
                                          Exp:{' '}
                                          {
                                            candidate.experience
                                          }
                                        </p>
                                      )}
                                    </div>

                                    <div className="d-flex gap-2 mt-auto">
                                      <SigniteButton
                                        variant="primary"
                                        className="flex-grow-1"
                                        onClick={() =>
                                          handleInitiateCall(
                                            candidate
                                          )
                                        }
                                        style={{
                                          fontSize:
                                            '0.8rem',
                                          padding:
                                            '6px',
                                        }}
                                      >
                                        <FontAwesomeIcon
                                          icon={faPhone}
                                          className="me-1"
                                        />
                                        Call
                                      </SigniteButton>

                                      <SigniteButton
                                        variant="outline-secondary"
                                        className="flex-grow-1 bg-white text-secondary border-secondary"
                                        onClick={() =>
                                          handleViewCallHistory(
                                            candidate
                                          )
                                        }
                                        style={{
                                          fontSize:
                                            '0.8rem',
                                          padding:
                                            '6px',
                                        }}
                                      >
                                        <FontAwesomeIcon
                                          icon={
                                            faHistory
                                          }
                                          className="me-1"
                                        />
                                        History
                                      </SigniteButton>
                                    </div>
                                  </Card.Body>
                                </Card>
                              </Col>
                            )
                          )}
                        </Row>
                      )}

                      {renderPagination()}
                    </div>
                  )}
                </Card.Body>
              </>
            ) : (
              <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted bg-light rounded-end">
                <FontAwesomeIcon
                  icon={faBuilding}
                  size="4x"
                  className="mb-3 opacity-25"
                />

                <h5 className="fw-normal">
                  No client selected
                </h5>

                <p>
                  Pick a client from the sidebar to view
                  their candidates.
                </p>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* AI Call Popup */}
      {isAICallPopupVisible &&
        selectedCandidateForCall && (
          <AICallPopup
            isVisible={isAICallPopupVisible}
            onClose={() => {
              setAICallPopupVisible(false);
              setSelectedCandidateForCall(null);
            }}
            candidateId={
              selectedCandidateForCall.candidateId
            }
            clientId={selectedClient?.clientId}
            onCall={handleAICallInitiated}
          />
        )}

      {/* Call Status Modal */}
      <CallStatusModal
        isVisible={isCallStatusModalVisible}
        onClose={handleCallStatusClose}
        callStatus={callStatusData}
        onRefresh={handleRefreshCallStatus}
        isLoading={isCallStatusLoading}
      />

      {/* Call History Modal */}
      <CallHistoryModal
        isVisible={isCallHistoryModalVisible}
        onClose={() =>
          setCallHistoryModalVisible(false)
        }
        calls={callHistoryData}
        onViewCallDetails={
          handleViewCallDetailsFromHistory
        }
        onRefreshCall={handleRefreshCallFromHistory}
        isLoading={isCallHistoryLoading}
        initialScrollTop={callHistoryScrollTop}
      />
    </Container>
  );
};

export default AdminAutomatedInterviews;